import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Select,
  message,
  Space,
  Form,
  Typography,
  Row,
  Col,
  Divider
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  BlockOutlined,
  FilePdfOutlined
} from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Method_POST,
  Method_POST_CONTEXTO,
  Method_POST_REST,
} from "../../../../service/api";
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

const { Option } = Select;
const { Title, Text } = Typography;


const ordenarPlanoContas = (a, b) => {
  const ca = String(a?.id || "").split(".").map(Number);
  const cb = String(b?.id || "").split(".").map(Number);

  for (let i = 0; i < Math.max(ca.length, cb.length); i++) {
    const diff = (ca[i] || 0) - (cb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

export default function ModalContasContabeis({ visible, onClose, title, zIndex, onFocus }) {
  const draggableProps = useDraggableModal(zIndex, onFocus, {
    visible,
    modalKey: 'contas',
    initialWidth: 1200,
    initialHeight: 'auto',
    overflow: 'hidden'
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("new");
  const [tipos, setTipos] = useState([]);

  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) fetchList();
    fetchTipos();
  }, [visible]);

  const fetchList = async (term = "") => {
    setLoading(true);
    try {
      const response = await Method_POST_REST("/contabil/conta/listar", {
        nome: term,
      });

      const lista = (Array.isArray(response) ? response : []).map(item => ({
        ...item,
        codigoReduzido: String(item.codigoReduzido || ""),
      }));

      setData(lista.sort(ordenarPlanoContas));
    } finally {
      setLoading(false);
    }
  };

  const fetchTipos = async () => {
    const response = await Method_POST_REST(
      "/contabil/conta/tipo/listar",
      {}
    );
    setTipos(response || []);
  };

  const handleSearch = () => fetchList(searchTerm);

  const handleNew = () => {
    setModalMode("new");
    form.resetFields();
    setFormModalVisible(true);
  };

  const handleEdit = async (record) => {
    setModalMode("edit");
    setFormModalVisible(true);

    try {
      const response = await Method_POST("/contabil/conta/consultar", {
        id: record.id,
      });

      form.setFieldsValue({
        id: response.id,
        nome: response.nome,
        codigo: response.codigo,
        codigoReduzido: response.codigoReduzido,
        tipo: response.tipo?.id,
      });
    } catch (error) {
      message.error("Erro ao consultar a conta.");
      form.setFieldsValue({
        id: record.id,
        nome: record.nome,
        codigo: record.codigo,
        codigoReduzido: record.codigoReduzido,
        tipo: record.tipo?.id,
      });
    }
  };

  const handleRemove = (record) => {
    Modal.confirm({
      title: "Confirmação",
      content:
        "Confirma a exclusão do registro e de todos os apontamentos associados a ele?",
      okText: "Sim",
      cancelText: "Não",
      onOk: async () => {
        await Method_POST_CONTEXTO("/contabil/conta/remover", {
          id: record.id,
        });
        message.success("Registro removido com sucesso");
        fetchList(searchTerm);
      },
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const url =
        modalMode === "edit"
          ? "/contabil/conta/atualizar"
          : "/contabil/conta/cadastrar";

      await Method_POST_CONTEXTO(url, {
        ...values,
        tipo: { id: values.tipo },
        lancamento: true,
      });

      message.success("Registro salvo com sucesso");
      setFormModalVisible(false);
      fetchList(searchTerm);
    } catch (err) {
      console.error("Erro ao salvar:", err);
    }
  };

  const handleDownloadPDF = () => {
    if (!data || data.length === 0) {
      message.warning("Não há dados para exportar.");
      return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const tableColumn = ["ID", "Código Completo", "Nome", "Cód. Reduzido"];
    const tableRows = [];

    data.forEach(item => {
      const nivel = String(item.codigoReduzido || "").split(".").length - 1;
      const indent = "   ".repeat(nivel); // Espaçamento para estrutura
      const rowData = [
        item.id,
        item.codigo,
        indent + item.nome,
        item.codigoReduzido
      ];
      tableRows.push(rowData);
    });

    // Título do documento
    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42);
    doc.text("Plano de Contas", 14, 15);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Gerado em: ${new Date().toLocaleString()}`, 14, 22);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 28,
      theme: 'grid',
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        halign: 'left',
        valign: 'middle'
      },
      headStyles: {
        fillColor: [0, 90, 158],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 40 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 35 }
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body') {
          const item = data[hookData.row.index];
          const snLancamento = item.sn_lancamento || item.snLancamento || item.lancamento;
          const isSintetica = snLancamento === 'N' || snLancamento === false;

          if (isSintetica) {
            hookData.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    doc.save("plano_de_contas.pdf");
    message.success("PDF gerado com sucesso!");
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 120,
    },
    {
      title: "Código Completo",
      dataIndex: "codigo",
      key: "codigo",
      width: 180,
    },
    {
      title: "Nome e Nível Contábil",
      dataIndex: "nome",
      key: "nome",
      render: (text, record) => {
        const nivel = String(record.codigoReduzido || "").split(".").length - 1;
        const color = nivel === 1 ? '#0f172a' : nivel === 2 ? '#3b82f6' : '#64748b';
        const fontWeight = nivel <= 2 ? 700 : 500;

        return (
          <span style={{
            paddingLeft: nivel * 24,
            color,
            fontWeight,
            display: 'inline-block',
            position: 'relative'
          }}>
            {nivel > 1 && (
              <span style={{
                position: 'absolute',
                left: (nivel - 1) * 24 + 8,
                top: '50%',
                width: 8,
                height: 1,
                backgroundColor: '#cbd5e1'
              }} />
            )}
            {text}
          </span>
        );
      },
    },
    {
      title: "Código Reduzido",
      dataIndex: "codigoReduzido",
      key: "codigoReduzido",
      width: 160,
    },
    {
      title: "Operações",
      key: "acoes",
      width: 200,
      render: (_, record) => {
        // Validação correta via banco de dados: apenas exibe botões se for conta analítica ('S' de Sim para lançamento)
        const snLancamento = record.sn_lancamento || record.snLancamento || record.lancamento;
        const isSintetica = snLancamento === 'N' || snLancamento === false;

        if (isSintetica) {
          return <Space><span style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>Conta Sintética</span></Space>;
        }

        return (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ color: '#005A9E', fontWeight: 600 }}
            >
              Editar
            </Button>
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleRemove(record)}
              style={{ fontWeight: 600 }}
            >
              Remover
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={title || "Plano de Contas"}
      footer={null}
      width={1300}
      className="premium-modal-v2"
      {...draggableProps}
      styles={{
        content: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        },
        body: {
          maxHeight: "80vh",
          padding: '24px',
          overflowY: "auto",
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <div style={{ padding: '8px 0', marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={8}>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Gerencie as contas sintéticas e analíticas da empresa.
            </Text>
          </Col>
          <Col xs={24} md={16} style={{ textAlign: 'right' }}>
            <Space>
              <Input
                placeholder="Pesquisar por nome"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  fetchList(e.target.value);
                }}
                onPressEnter={handleSearch}
                style={{ width: 280, borderRadius: 8, height: 42 }}
                prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              />
              <Button
                type="primary"
                onClick={handleSearch}
                style={{ height: 42, borderRadius: 8, fontWeight: 600, padding: '0 24px', backgroundColor: '#005A9E', borderColor: '#005A9E' }}
              >
                Buscar
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleNew}
                style={{ height: 42, borderRadius: 8, fontWeight: 600, padding: '0 24px', backgroundColor: '#005A9E', borderColor: '#005A9E' }}
              >
                Nova Conta
              </Button>
              <Button
                icon={<FilePdfOutlined />}
                onClick={handleDownloadPDF}
                style={{ height: 42, borderRadius: 8, fontWeight: 600, padding: '0 24px', color: '#dc2626', borderColor: '#fecaca' }}
              >
                Exportar PDF
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '250']
          }}
          size="middle"
        />
      </div>

      <Modal
        open={formModalVisible}
        title={null}
        onCancel={() => setFormModalVisible(false)}
        footer={null}
        destroyOnClose
        className="premium-modal-v2"
        width={600}
      >
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            backgroundColor: '#f0f9ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#0284c7',
            fontSize: 24,
          }}>
            <BlockOutlined />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: '#050913ff' }}>
              {modalMode === "new" ? "Cadastrar Conta" : "Editar Conta Corrente"}
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Preencha as informações para registrar na base estrutural.
            </Text>
          </div>
        </div>

        <Divider style={{ margin: '16px 0 24px 0' }} />

        <Form form={form} layout="vertical">
          <Form.Item label="ID" name="id">
            <Input disabled />
          </Form.Item>

          <Form.Item label="Nome" name="nome" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item
            label="Código Completo"
            name="codigo"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Código Reduzido"
            name="codigoReduzido"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Tipo" name="tipo" rules={[{ required: true }]}>
            <Select
              placeholder="Selecione o grupo/tipo da conta"
              showSearch
              optionFilterProp="children"
            >
              {tipos.map((t) => (
                <Option key={t.id} value={t.id}>
                  <span style={{ fontWeight: 600, marginRight: 8, color: '#64748b' }}>{t.id}</span>
                  {t.nome}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 32 }}>
            <Button onClick={() => setFormModalVisible(false)} size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
              Cancelar
            </Button>
            <Button type="primary" onClick={handleSave} size="large" style={{ borderRadius: 8, fontWeight: 600, padding: '0 32px' }}>
              Salvar Alterações
            </Button>
          </div>
        </Form>
      </Modal>
    </Modal>
  );
}
